/**
 * Parafon sözleşmeleri — şeritler arası tek doğruluk kaynağı.
 *
 * KURAL: Bu dosya bir aşamanın ortasında değişmez. Şeritler (A toplayıcılar,
 * B merkez, C yerel çekirdek, D senkron, F analiz) bu tiplere göre paralel
 * çalışır. Değişiklik gerekiyorsa aşama kapısında yapılır.
 */

// ---------------------------------------------------------------------------
// Temel
// ---------------------------------------------------------------------------

/** ISO 4217 (TRY, USD, EUR) ya da kripto kodu (BTC, ETH). */
export type CurrencyCode = string;

/**
 * DEFTERE GİRİLEBİLECEK PARA BİRİMLERİ — kapalı liste.
 *
 * Niçin var: `CurrencyCode` bir `string` ve öyle KALIYOR. Daraltmak, eski
 * bir `.pfai` defterinde duran kodu tip düzeyinde geçersiz kılar ve dosyayı
 * okunamaz hâle getirirdi; oysa okunamaz bir defter, yanlış bir defterden
 * beterdir. Bu liste bir TİP kısıtı değil, bir GİRİŞ kısıtıdır: yeni kayıt
 * yalnızca buradan seçilir, eskisi okunur ve damgalanır.
 *
 * Listenin sınırı keyfî değil: merkez kuru TCMB'den alıyor ve TCMB'nin
 * yayımladığı kod kümesi bu. Listede olmayan bir kod girilebilseydi o
 * pozisyonun kuru HİÇBİR ZAMAN bulunamaz, rapor da sebebini söyleyemeden
 * eksik çıkardı — "TL" yazılınca tam olarak bu oldu.
 *
 * Sıra ekranda göründüğü sıradır: önce fiilen kullanılanlar, sonra alfabetik.
 */
/**
 * RAPOR PARA BİRİMLERİ — raporun, grafiğin ve Dönemsel Getiri'nin
 * istenebileceği birimler; TEK kaynak (kullanıcı kararı 27.10.2026).
 * YALNIZCA TCMB günlük kur bülteninde alış kuru yayımlanan birimler
 * açılabilir: kanonik kur TCMB, ikinci bir kur kaynağı eklenmez (merkezdeki
 * ECB bağlayıcısı yalnızca geçmişi toplu çeker). Yeni birim açmak bu listeye
 * bir kod eklemektir: seçici, ad (dilin yerleşik para birimi adı) ve
 * eşitlemenin `[kod, TRY]` kuru kendiliğinden gelir. Dil ile para birimi
 * bağımsızdır: varsayılan dile göre değişmez.
 */
export const RAPOR_PARA_BIRIMLERI = ["TRY", "USD", "EUR"] as const satisfies readonly CurrencyCode[];

export const CURRENCIES: readonly { readonly code: CurrencyCode; readonly name: string }[] = [
  { code: "TRY", name: "Türk lirası" },
  { code: "USD", name: "ABD doları" },
  { code: "EUR", name: "Euro" },
  { code: "GBP", name: "İngiliz sterlini" },
  { code: "CHF", name: "İsviçre frangı" },
  { code: "JPY", name: "Japon yeni" },
  { code: "AED", name: "BAE dirhemi" },
  { code: "AUD", name: "Avustralya doları" },
  { code: "AZN", name: "Azerbaycan manatı" },
  { code: "BGN", name: "Bulgar levası" },
  { code: "CAD", name: "Kanada doları" },
  { code: "CNY", name: "Çin yuanı" },
  { code: "DKK", name: "Danimarka kronu" },
  { code: "KRW", name: "Güney Kore wonu" },
  { code: "KWD", name: "Kuveyt dinarı" },
  { code: "NOK", name: "Norveç kronu" },
  { code: "PKR", name: "Pakistan rupisi" },
  { code: "QAR", name: "Katar riyali" },
  { code: "RON", name: "Romen leyi" },
  { code: "RUB", name: "Rus rublesi" },
  { code: "SAR", name: "Suudi riyali" },
  { code: "SEK", name: "İsveç kronu" },
];

/** Kapalı listedeki kodlar — hızlı bakış için. */
export const CURRENCY_CODES: ReadonlySet<CurrencyCode> = new Set(CURRENCIES.map((c) => c.code));

/**
 * Bu kod deftere YENİ girilebilir mi?
 *
 * Eski kayıtları yargılamak için de kullanılıyor, ama orada sonucu bir RET
 * değil bir DAMGA: `false` dönmesi "bu satır atılsın" demek değil, "bu
 * satırın kuru bulunamayacak, kullanıcıya söylenmeli" demek.
 */
export function isKnownCurrency(code: string): boolean {
  return CURRENCY_CODES.has(code.trim().toUpperCase());
}

/**
 * Sık yapılan yazımların karşılığı — damgada "bunu mu demek istediniz" için.
 *
 * Liste kısa ve KASITLI olarak kısa: amaç her yanlışı tahmin etmek değil,
 * kullanıcının gerçekten yaptığı yanlışı adıyla çağırmak. "TL" burada çünkü
 * Türkçede para biriminin günlük adı bu ve ISO kodu olan "TRY" ile
 * karıştırılması kaçınılmaz — nitekim karıştırıldı.
 */
const CURRENCY_TYPOS: Readonly<Record<string, CurrencyCode>> = {
  TL: "TRY",
  TRL: "TRY",
  "₺": "TRY",
  TURKLIRASI: "TRY",
  EURO: "EUR",
  "€": "EUR",
  DOLAR: "USD",
  USDOLAR: "USD",
  $: "USD",
  STERLIN: "GBP",
  "£": "GBP",
};

/** Tanınmayan kodun muhtemel karşılığı; tahmin edilemiyorsa `null`. */
export function suggestCurrency(code: string): CurrencyCode | null {
  return CURRENCY_TYPOS[code.trim().toUpperCase()] ?? null;
}

/** ISO tarih, gün hassasiyetinde: "2026-09-03". Zaman dilimi taşımaz. */
export type IsoDate = string;

/** ISO 8601 zaman damgası, UTC: "2026-09-03T10:15:00Z". */
export type IsoTimestamp = string;

export type AssetClass =
  | "equity_tr" // BIST hissesi
  | "equity_foreign" // yabancı hisse
  | "etf"
  | "crypto"
  | "fund_tr" // TEFAS yatırım fonu
  | "commodity" // altın, gümüş
  | "fx" // döviz pozisyonu
  | "deposit" // mevduat / faiz
  | "other";

/**
 * Ondalık sayılar string olarak taşınır. Finansal hesapta IEEE-754 kayan nokta
 * kabul edilemez: 0.1 + 0.2 !== 0.3 hatası kuruş kaybına dönüşür. Analiz motoru
 * bunları ondalık kütüphanesiyle okur.
 */
export type Decimal = string;

// ---------------------------------------------------------------------------
// Katalog — geniş, ucuz, fiyat içermez
// ---------------------------------------------------------------------------

/** Merkezde bir aracın kanonik kimliği. */
export type InstrumentId = string;

export interface CatalogEntry {
  symbol: string;
  /** Borsa/piyasa kodu: "BIST", "NASDAQ", "BINANCE", "TEFAS". */
  exchange: string;
  assetClass: AssetClass;
  currency: CurrencyCode;
  name: string;
  isin?: string;
  /** Kaynağın kendi sembolü — takma ad tablosuna yazılır (örn. "THYAO.IS"). */
  sourceSymbol: string;
  metadata?: Record<string, unknown>;
}

/** Çözümlenmiş araç referansı; toplayıcıya verilen giriş. */
export interface InstrumentRef {
  instrumentId: InstrumentId;
  symbol: string;
  exchange: string;
  assetClass: AssetClass;
  currency: CurrencyCode;
  /** Bu kaynağın bu aracı hangi sembolle tanıdığı. */
  sourceSymbol: string;
}

/** /resolve yanıtındaki aday. */
export interface ResolveCandidate extends InstrumentRef {
  name: string;
  isin?: string;
  /** 0..1 — arama eşleşme güveni. Belirsizse kullanıcı seçer. */
  score: number;
}

// ---------------------------------------------------------------------------
// Arşiv — yalnızca izlemeye alınmış araçlar
// ---------------------------------------------------------------------------

export interface DailyBar {
  date: IsoDate;
  open: Decimal;
  high: Decimal;
  low: Decimal;
  close: Decimal;
  /** Şirket olaylarına göre düzeltilmiş kapanış; kaynak vermiyorsa yok. */
  adjClose?: Decimal;
  volume?: Decimal;
}

export interface SpotQuote {
  instrumentId: InstrumentId;
  ts: IsoTimestamp;
  price: Decimal;
  currency: CurrencyCode;
  /**
   * SEANS DIŞI FİYAT (04.11.2026): dolu ise bu fiyat normal seansın değil,
   * seans öncesi (`pre`) ya da sonrası (`post`) işlemin; alan yoksa normal
   * seans fiyatı. Merkez ikisini ayrı saklar.
   */
  seans?: "pre" | "post" | "night";
}

export type CorporateActionType =
  | "dividend" // temettü
  | "split" // bölünme
  | "bonus_issue" // bedelsiz
  | "rights_issue" // bedelli / rüçhan
  | "merger"
  | "delisting";

export interface CorporateAction {
  instrumentId: InstrumentId;
  exDate: IsoDate;
  type: CorporateActionType;
  /**
   * Bölünme/bedelsiz oranı kesir olarak. Yahoo numerator/denominator verir,
   * KAP yüzde verir; ikisi de buraya kesir olarak yazılır ve bölme analiz
   * motorunda ondalık kütüphanesiyle yapılır — burada float'a çevrilmez.
   */
  ratioNumerator?: Decimal;
  ratioDenominator?: Decimal;
  /** Temettüde pay başına brüt tutar. */
  amount?: Decimal;
  currency?: CurrencyCode;
  raw?: Record<string, unknown>;
}

/**
 * Ham katman: toplayıcı kanonik kimliği bilmez, onu merkez atar. Bağlayıcılar
 * bu tipleri döndürür; merkez `sourceSymbol` üzerinden takma ad tablosuna
 * bakıp `instrumentId` bağlar.
 */
export type Raw<T extends { instrumentId: InstrumentId }> = Omit<T, "instrumentId"> & {
  sourceSymbol: string;
};

export type RawCorporateAction = Raw<CorporateAction>;
export type RawFundNav = Raw<FundNav>;
export type RawSpotQuote = Raw<SpotQuote>;

export interface FxRate {
  base: CurrencyCode;
  quote: CurrencyCode;
  date: IsoDate;
  /**
   * Kanonik kur: 1 birim base = rate birim quote.
   * TCMB bazı kurları 100 birim üzerinden yayınlar (100 JPY = x TRY); toplayıcı
   * bunu string üzerinde ondalık kaydırarak 1 birime indirger ve `sourceUnit`
   * alanında ne yaptığını kayda geçirir. Arşivde daima 1 birim durur.
   */
  rate: Decimal;
  /** Kaynağın yayınladığı birim (TCMB JPY için 100). Normalleştirme kanıtı. */
  sourceUnit?: number;
  /** Kaynak alış/satış makası veriyorsa. ECB/Frankfurter tek kur verir → yok. */
  rateBuy?: Decimal;
  rateSell?: Decimal;
}

export interface FundNav {
  instrumentId: InstrumentId;
  date: IsoDate;
  nav: Decimal;
  totalValue?: Decimal;
  investorCount?: number;
}

// ---------------------------------------------------------------------------
// Kapsam — "eksik veriyi sıfır sanma" kuralının taşıyıcısı
// ---------------------------------------------------------------------------

/**
 * Bir araç için elimizde hangi aralığın olduğunu söyler. Analiz motoru bunu
 * okumadan hesap yapmaz; kapsam dışı dönem için sayı değil hata döner.
 */
export interface Coverage {
  instrumentId: InstrumentId;
  /** Geçmiş buraya kadar tam. null = hiç veri yok. */
  from: IsoDate | null;
  to: IsoDate | null;
  /** Aralık içindeki bilinen boşluklar (kaynak eksikleri, tatil değil). */
  gaps: Array<{ from: IsoDate; to: IsoDate }>;
  state: TrackState;
  /**
   * GEÇMİŞ HEDEFİNİN TABANI — "bu tarihten öncesi hesaplanamaz".
   *
   * Aşama 3'ün ihtiyacı (plan madde 5): rapor bir dönem için sayı üretemiyorsa
   * SEBEBİNİ söylemek zorunda. `from` "elimizde bu kadarı var" diyor;
   * `horizonFrom` ise "daha fazlası HİÇ GELMEYECEK" diyor ve ikisi bambaşka
   * cümleler. `from` geriye doğru büyümeye devam edebilir (geri doldurma
   * sürüyor), `horizonFrom` ise sınırın kendisi.
   *
   * Bu bilgi bir dönem YALNIZCA `/track` yanıtında vardı; rapor `/prices`
   * okuyor ve "şu tarihten öncesi kapsam dışı" diyebilmek için ikinci bir
   * çağrı yapmak zorunda kalıyordu — ya da (daha kötüsü) eksikliği
   * "veri yok" diye gösteriyordu.
   *
   * `null` = araç izlenmiyor (talep yok) ya da katalogda değil: iddia YOK.
   * Boş bir tarih uydurmak yerine yokluk açıkça taşınıyor.
   */
  horizonFrom: IsoDate | null;
}

export type TrackState =
  | "pending" // talep alındı, henüz veri yok
  | "spot_ready" // anlık + son 30 gün hazır → panel çalışır
  | "backfilling" // geçmiş geriye doğru doluyor
  | "ready" // ufka kadar tam
  | "dormant" // hareketi kalmadı, yenileme durdu
  | "unresolved"; // hiçbir kaynak çözemedi → elle değerleme

// ---------------------------------------------------------------------------
// Connector — her veri kaynağı bunu uygular
// ---------------------------------------------------------------------------

/**
 * Bir limit değerinin nereden geldiği. Kuyruk planlaması ölçülmüş bir sayıya
 * güvenir, varsayıma güvenmez — ikisinin bağlayıcıda ayırt edilememesi Aşama 0'da
 * tespit edilen bir eksikti.
 */
export type LimitProvenance =
  | "measured" // uç noktaya istek atılarak ölçüldü
  | "structural" // uç noktanın biçiminden çıkar (toplu çağrı desteği yok gibi)
  | "assumed" // belgeye/deneyime dayanan varsayım — doğrulanmadı
  | "unmeasured"; // ölçülmedi (ağ kapalıydı, anahtar yoktu)

export interface SourceLimits {
  /** Dakikada izin verilen istek. Kuyruk bütçesi bunu kullanır. */
  reqPerMin: number;
  /**
   * Tek çağrının döndürebildiği azami gün sayısı. Geri doldurmanın kaç parçaya
   * bölüneceğini bu belirler — Aşama 0'da ölçülmesi gereken kritik sayı.
   */
  maxDaysPerCall: number;
  /**
   * Bazı kaynakta sınır gün değil SATIR sayısıdır (Binance 1000 mum,
   * Twelve Data 5000 satır). Günlük barda ikisi çakışır ama daha sık
   * aralıklarda ayrışır; kuyruk ikisinin de küçüğünü kullanır.
   */
  rowCap?: number | null;
  /**
   * KAYNAĞIN GERİYE DÖNÜK DERİNLİĞİ, yıl cinsinden. Bundan eskisini istemek
   * veri getirmez; kaynak reddeder.
   *
   * CANLI ÜRETİMDE BULUNDU (08.09.2026). TEFAS, 5 yıldan eski bir başlangıç
   * tarihi için HTTP 200 döndürüp gövdede hata bildiriyor:
   *   "Geçersiz veri: Baslangıc Tarihi 5 yıldan eski olamaz"
   * 10 yıllık ufukla ~122 geçmiş işinin yarısı böylece HİÇBİR ZAMAN başarılı
   * olamıyordu: her deneme bir istek yakıyor (429'ların bir bölümü bundan),
   * su işareti ufka ulaşamadığı için fon sonsuza kadar `backfilling` kalıyor
   * ve en kötüsü, 5 yıllık bir geçmişi "on yıl tam" diye göstermek sessiz bir
   * yalan oluyordu.
   *
   * `maxDaysPerCall`ten FARKLI BİR EKSEN: o "tek çağrı ne kadar genişlik
   * taşır" (parçalama), bu "arşivin tabanı nerede" (ufuk). Biri parça sayısını,
   * diğeri ufkun kendisini belirliyor; tek sayıyla ifade edilemezler.
   *
   * Tanımsız/null = bilinen bir taban yok, ufku ihraç tarihi ve hedef belirler.
   * Sınır bir OLGU olarak burada duruyor ki merkez ufku kırpabilsin ve
   * kırpıldığı gerçeği kullanıcıya söylenebilsin.
   */
  maxHistoryYears?: number | null;
  /** Tek çağrıda kaç araç sorgulanabilir (anlık fiyat için toplu çağrı). */
  maxSymbolsPerCall: number;
  /** API anahtarı gerekiyor mu. Bu bizim yapılandırmamızın olgusu, ölçüm değil. */
  requiresKey: boolean;
  /** Alan başına dayanak. Eksik alan "unmeasured" sayılır. */
  provenance?: Partial<Record<keyof Omit<SourceLimits, "provenance">, LimitProvenance>>;
}

export interface HealthReport {
  ok: boolean;
  latencyMs: number;
  /** Kaynak yaşıyor ama beklenenden farklı davranıyorsa açıklama. */
  note?: string;
}

/**
 * `dailyWithActions()`in dönüşü. İki listenin AYNI yanıttan geldiği burada
 * tipe yazılı: ayrı ayrı döndürülselerdi çağıran onları iki ölçüm sanabilir
 * ve iki kez bütçelerdi.
 */
export interface DailyWithActions {
  readonly bars: readonly DailyBar[];
  readonly actions: readonly CorporateAction[];
  /**
   * Kaynağın AYNI yanıtta beyan ettiği para birimi (Yahoo chart
   * `meta.currency`). Katalog aramasının bilmediği para birimini merkez
   * buradan öğrenir (`learnCurrency`). Kaynak vermiyorsa yok.
   */
  readonly currency?: CurrencyCode;
}

export interface Connector {
  readonly id: string;
  readonly assetClasses: readonly AssetClass[];
  readonly limits: SourceLimits;

  /** Katalog: geniş araç listesi, fiyat yok. Haftada bir çalışır. */
  catalog?(): AsyncIterable<CatalogEntry>;

  /** Serbest metinden aday araçlar. Belirsizlik normaldir; kullanıcı seçer. */
  resolve(query: string): Promise<ResolveCandidate[]>;

  /** İhraç/işlem başlangıcı — 10 yıl ufkunun tabanı. Bilinmiyorsa null. */
  inception?(ref: InstrumentRef): Promise<IsoDate | null>;

  /** Toplu anlık fiyat. Yaşam döngüsünün ilk adımı. */
  spot?(refs: readonly InstrumentRef[]): Promise<SpotQuote[]>;

  /** Gün sonu barları. from..to kapalı aralık, maxDaysPerCall'ı aşmamalı. */
  daily(ref: InstrumentRef, from: IsoDate, to: IsoDate): Promise<DailyBar[]>;

  /** Şirket olayları. Bunlar olmadan BIST getirisi yanlış çıkar. */
  actions?(ref: InstrumentRef, from: IsoDate, to: IsoDate): Promise<CorporateAction[]>;

  /**
   * BAR VE OLAY TEK YANITTAN — hız bütçesinin doğru sayabilmesi için.
   *
   * BULUNAN KUSUR: işçi araç bazlı bir işte `daily()` ve `actions()`ı AYRI
   * AYRI çağırıyordu, yani İKİ HTTP isteği; oysa kuyruk iş başına bütçeden
   * BİR jeton harcıyor (`queue.consume`). Yahoo'da ölçülmüş `reqPerMin = 60`
   * fiilen ~120 istek/dakika üretiyordu. Tavanın altında kaldığı için
   * patlamadı ama `provenance: "measured"` etiketi doğruyu söylemiyordu — ve
   * bu, `chart()` içinde zaten yazılı olan kuralın ihlaliydi: *bağlayıcı
   * kendi içinde çağrı çoğaltırsa ölçülmüş bütçe sessizce katlanır.*
   *
   * İkinci istek üstelik BOŞUNAYDI: Yahoo'nun tek `chart` yanıtı hem barları
   * hem `events` nesnesini taşıyor, yani `actions()` aynı belgeyi ikinci kez
   * indirip yarısını atıyordu.
   *
   * NİÇİN `daily()`nin imzası DEĞİŞMEDİ: yayınlanmış bir üyeyi bozardı ve
   * olay sunmayan beş bağlayıcıyı da gereksizce ilgilendirirdi. Ek ve isteğe
   * bağlı üye, `navAll()` precedentinin aynısı (bkz. `worker/runner.ts`).
   *
   * KURAL: `actions()` sunan bir bağlayıcı bunu da sunmak ZORUNDA —
   * `createRegistry()` aksini AÇILIŞTA fırlatıyor. Böylece "iki istek, bir
   * jeton" durumu yapılandırma hatası olarak ölür, üretimde sessiz kalmaz.
   *
   * Uygulayan kaynakta işçi YALNIZCA bunu çağırır; `daily()`/`actions()` o
   * işte hiç çağrılmaz — çift indirme olurdu.
   */
  dailyWithActions?(
    ref: InstrumentRef,
    from: IsoDate,
    to: IsoDate,
  ): Promise<DailyWithActions>;

  /** Kur kaynakları bunu uygular (TCMB, ECB). */
  fx?(pairs: ReadonlyArray<[CurrencyCode, CurrencyCode]>, from: IsoDate, to: IsoDate): Promise<FxRate[]>;

  health(): Promise<HealthReport>;
}

// ---------------------------------------------------------------------------
// Geri doldurma kuyruğu
// ---------------------------------------------------------------------------

export type BackfillKind = "spot" | "recent" | "history";

/** Öncelik küçükten büyüğe işlenir: tüm spot'lar, herhangi bir history'den önce. */
export const BACKFILL_PRIORITY: Record<BackfillKind, number> = {
  spot: 0,
  recent: 1,
  history: 2,
};

export interface BackfillJob {
  id: string;
  instrumentId: InstrumentId;
  kind: BackfillKind;
  from: IsoDate;
  to: IsoDate;
  priority: number;
  attempts: number;
  nextAttemptAt: IsoTimestamp;
}

/** İzleme talebi. Bilinçli olarak tarih ve kullanıcı kimliği TAŞIMAZ. */
export interface TrackRequest {
  instrumentId: InstrumentId;
}

export interface TrackStatus {
  instrumentId: InstrumentId;
  state: TrackState;
  /** Ufuk: bugün − 10 yıl, ya da ihraç tarihi (hangisi sonraysa). */
  horizonFrom: IsoDate;
  /** Su işareti: geçmiş buraya kadar tam. */
  backfilledTo: IsoDate | null;
  reason: "demand" | "seed" | "benchmark";
  lastError?: string;
}

/** Sabit ufuk kararı: talep tarih taşımadığı için sızıntı yok. */
export const HISTORY_HORIZON_YEARS = 10;

/** Hareketi kalmayan araç bu süre sorgusuz kalırsa silinir. */
export const PURGE_AFTER_DORMANT_DAYS = 365;

// ---------------------------------------------------------------------------
// Yerel hareket defteri — senkronun taşıdığı birim
// ---------------------------------------------------------------------------

export type TransactionType =
  | "BUY"
  | "SELL"
  | "DIVIDEND"
  | "INTEREST"
  | "SPLIT"
  | "BONUS"
  | "RIGHTS"
  | "DEPOSIT"
  | "WITHDRAWAL"
  | "FEE"
  | "TRANSFER_IN"
  | "TRANSFER_OUT"
  /** Vergi ödemesi (yıllık beyan vb.; kullanıcı kararı 19.10.2026). */
  | "TAX_PAYMENT"
  /**
   * DÖVİZ ALIŞ / SATIŞ (kullanıcı kararı 22.10.2026): aynı hesabın içinde bir
   * para biriminden ötekine geçiş. Para girişi ya da çıkışı DEĞİL: verilen
   * para birimindeki nakit azalır, alınan para birimindeki artar.
   */
  | "FX_CONVERSION";

/** Vergi ödemesinin kaynağı: hesaptaki nakitten mi, dışarıdan mı. */
export const VERGI_KAYNAKLARI = ["nakit", "disaridan"] as const;
export type VergiKaynagi = (typeof VERGI_KAYNAKLARI)[number];

/**
 * Bir hareket ya merkezi bir araca ya da yalnızca elle değerlenen bir varlığa
 * bağlanır. Bu ayrım Aşama 1'de kurulur; Aşama 2'deki izleme durumları bunun
 * üstüne oturur.
 */
export type AssetBinding =
  | { kind: "tracked"; instrumentId: InstrumentId }
  | { kind: "manual"; label: string; currency: CurrencyCode };

/**
 * lamport ve deviceId senkronun temelidir ve Aşama 1'de yerine konur.
 * Sonradan eklemek tüm senkron katmanını yeniden yazmak demektir.
 */
export interface Transaction {
  id: string; // ULID — zaman sıralı
  accountId: string;
  asset: AssetBinding;
  type: TransactionType;
  tradeDate: IsoDate;
  settleDate?: IsoDate;
  quantity: Decimal;
  price: Decimal;
  currency: CurrencyCode;
  fee?: Decimal;
  tax?: Decimal;
  /** Kullanıcı TCMB kurunu geçersiz kılmak isterse. */
  fxRateOverride?: Decimal;
  note?: string;
  /**
   * GERİ YATIRILAN TEMETTÜ (kullanıcı kararı 19.10.2026): bu ALIŞIN tamamı
   * temettüyle yapıldı. Maliyete girer (elde olanın maliyeti), ama "geçmiş
   * dahil getiri %"nin paydasına ("cepten çıkan para") GİRMEZ. Yalnızca
   * `BUY`da; yoksa `false`.
   */
  temettuden?: boolean;
  /**
   * VERGİ ÖDEMESİ (`TAX_PAYMENT`, kullanıcı kararı 19.10.2026): verginin ait
   * olduğu yıl ve ödemenin kaynağı. Yalnızca `TAX_PAYMENT`ta, orada zorunlu.
   * `nakit`: hesaptaki nakit azalır. `disaridan`: motor aynı gün aynı tutarda
   * bir para girişi sayar (nakit değişmez; asıl oranın paydası büyür).
   * Ödenecek vergi tahmin edilmez; yalnızca yazılan ödeme.
   */
  vergiYili?: number;
  vergiKaynagi?: VergiKaynagi;
  /**
   * DÖVİZ ALIŞ / SATIŞ (`FX_CONVERSION`, kullanıcı kararı 22.10.2026).
   * Verilen taraf her hareketteki gibi: `currency` + `quantity = "1"`,
   * `price = verilen tutar` (masraf `fee`). Alınan taraf bu iki alanda; ikisi
   * de yalnızca `FX_CONVERSION`da ve orada zorunlu, `alinanParaBirimi`
   * `currency`den farklı. Kur saklanmaz: iki tutarın oranıdır (kurumun
   * yuvarladığı kur yeniden çarpılmaz).
   */
  alinanTutar?: Decimal;
  alinanParaBirimi?: CurrencyCode;

  // --- senkron alanları ---
  lamport: number;
  deviceId: string;
  deleted: boolean;
  updatedAt: IsoTimestamp;
}

/**
 * Çakışma çözümü: yüksek lamport kazanır, eşitlikte deviceId sabit sıra verir.
 * Dört taşıyıcının da aynı nihai duruma ulaşmasının tek sebebi budur.
 */
export function transactionWins(a: Transaction, b: Transaction): boolean {
  if (a.lamport !== b.lamport) return a.lamport > b.lamport;
  return a.deviceId > b.deviceId;
}
